import React, { useState } from 'react';

const ForgotPassword: React.FC<{ onNavigate: (page: string) => void }> = ({ onNavigate }) => {
    const [step, setStep] = useState<1 | 2>(1);
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const baseUrl = (process.env.REACT_APP_API_ENDPOINT || '').replace(/\/$/, '');

    const handleRequestCode = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${baseUrl}/auth/forgot-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            });
            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.error || 'Failed to send reset code');
            }
            setStep(2);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmReset = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${baseUrl}/auth/reset-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, code, password: newPassword })
            });
            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.error || 'Failed to reset password');
            }
            setSuccess(true);
            setTimeout(() => onNavigate('login'), 3000);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full bg-white rounded-[2.5rem] shadow-2xl p-10 border border-slate-100">
                <div className="text-center mb-8">
                    <h2 className="text-3xl font-black text-slate-900 uppercase tracking-tight">
                        {success ? 'Success!' : step === 1 ? 'Forgot Password' : 'Reset Password'}
                    </h2>
                    <p className="text-slate-500 mt-2 font-medium text-sm">
                        {success 
                            ? 'Your password has been updated. Redirecting to login...' 
                            : step === 1 
                                ? 'Enter your email to receive a reset code.' 
                                : 'Enter the code sent to your email and your new password.'}
                    </p>
                </div>

                {error && (
                    <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 text-xs font-bold rounded-r-lg">
                        ⚠️ {error}
                    </div>
                )}

                {success ? (
                    <div className="flex justify-center py-8">
                        <div className="text-6xl animate-bounce">✨</div>
                    </div>
                ) : step === 1 ? (
                    <form onSubmit={handleRequestCode} className="space-y-6">
                        <div>
                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Email Address</label>
                            <input 
                                type="email" 
                                required 
                                className="w-full px-6 py-4 bg-slate-50 border-none rounded-2xl focus:ring-4 focus:ring-blue-500/10 font-bold text-slate-900" 
                                placeholder="name@company.com"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                            />
                        </div>
                        <button 
                            type="submit" 
                            disabled={loading}
                            className="w-full py-5 bg-slate-900 text-white rounded-2xl hover:bg-black font-black text-xs uppercase tracking-[0.3em] shadow-2xl transition-all disabled:opacity-50"
                        >
                            {loading ? 'Sending...' : 'Send Reset Code'}
                        </button>
                        <button 
                            type="button"
                            onClick={() => onNavigate('login')}
                            className="w-full text-xs font-bold text-slate-400 hover:text-slate-600 transition-colors uppercase tracking-widest"
                        >
                            Back to Login
                        </button>
                    </form>
                ) : (
                    <form onSubmit={handleConfirmReset} className="space-y-6">
                        <div>
                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Verification Code</label>
                            <input 
                                type="text" 
                                required 
                                className="w-full px-6 py-4 bg-slate-50 border-none rounded-2xl focus:ring-4 focus:ring-blue-500/10 font-bold text-slate-900 text-center tracking-[0.5em]" 
                                placeholder="000000"
                                value={code}
                                onChange={e => setCode(e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">New Password</label>
                            <input 
                                type="password" 
                                required 
                                className="w-full px-6 py-4 bg-slate-50 border-none rounded-2xl focus:ring-4 focus:ring-blue-500/10 font-bold text-slate-900" 
                                placeholder="••••••••"
                                value={newPassword}
                                onChange={e => setNewPassword(e.target.value)}
                            />
                        </div>
                        <button 
                            type="submit" 
                            disabled={loading}
                            className="w-full py-5 bg-blue-600 text-white rounded-2xl hover:bg-blue-700 font-black text-xs uppercase tracking-[0.3em] shadow-2xl transition-all disabled:opacity-50"
                        >
                            {loading ? 'Updating...' : 'Update Password'}
                        </button>
                        <button 
                            type="button"
                            onClick={() => setStep(1)}
                            className="w-full text-xs font-bold text-slate-400 hover:text-slate-600 transition-colors uppercase tracking-widest"
                        >
                            Resend Code
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
};

export default ForgotPassword;
